import React, { memo, forwardRef, useEffect, useMemo, useRef } from "react";
import "./SimpleExcelViewer.css";
import { toColumnLabel } from "./utils/columnUtils";

import FormulaBar from "./components/FormulaBar";
import Grid from "./components/Grid";
import SheetTabs from "./components/SheetTabs";
import { HeaderContextMenu, FreezeContextMenu } from "./components/ContextMenus";

import useWorkbookLoader from "./hooks/useWorkbookLoader";
import useSheetModel from "./hooks/useSheetModel";
import useCellSelection from "./hooks/useCellSelection";
import useFilters from "./hooks/useFilters";
import useHiddenRowsCols from "./hooks/useHiddenRowsCols";
import useResize from "./hooks/useResize";
import useFreezePanes from "./hooks/useFreezePanes";
import useScrollMemory from "./hooks/useScrollMemory";
import useKeyboardNav from "./hooks/useKeyboardNav";
import useHyperlinks from "./hooks/useHyperlinks";
import useHeaderContextMenu from "./hooks/useHeaderContextMenu";
import useZoom from "./hooks/useZoom";
import usePrint from "./hooks/usePrint";

/**
 * SimpleExcelViewer — orchestrator.
 *
 * This component is intentionally thin: every piece of behaviour lives
 * in its own custom hook under ./hooks/* and every piece of UI in
 * ./components/*.  This file just wires them together.
 */
const SimpleExcelViewer = forwardRef(({
  documentId, pageNumber, openId, userId,
  fileId,                       // back-compat alias for documentId
  excelPayload,                 // { excelData (base64), fileType, fileName } from DocumentRPD
  height = "calc(87vh - 62px)",
  zoom = 1,
  zoomScale: zoomScaleProp,
  onLocked,
}, ref) => {
  const effectiveDocumentId = documentId || fileId;
  const componentRef = useRef(null);

  // ---- Workbook loading & sheet model ----
  // NOTE: The hook no longer issues its own /images/ request.  It consumes
  // the payload supplied by DocumentRPD's single fetch.
  const {
    loading, error, sheetNames, sheetDataByName, setSheetDataByName,
    workbookBufferRef,
  } = useWorkbookLoader({ excelPayload, onLocked });

  const {
    activeSheet, setActiveSheet, prevSheetRef,
    activeModel, normalizedRows, maxCols,
    colWidths, rowHeights, cellStyles, cellRichHtml,
    sheetImages, sheetCharts, cellMerges, cellLinks,
  } = useSheetModel({ sheetNames, sheetDataByName });

  // ---- Selection / filtering / hidden rows-cols ----
  const {
    selectedCell, setSelectedCell, setSelection,
    selectionType, normalizedSelection,
    startSelection, extendSelection, selectColumn, selectRow,
  } = useCellSelection({ normalizedRows, maxCols });

  const {
    hiddenCols, hiddenRows,
    handleHideCol, handleUnhideCols, handleHideRow, handleUnhideRows,
    isLastVisibleBeforeHidden, isLastVisibleBeforeHiddenRow,
  } = useHiddenRowsCols({ activeSheet, prevSheetRef });

  const {
    filters, activeFilterCol, setActiveFilterCol,
    toggleFilterMenu, applyFilter, isRowVisible,
  } = useFilters({ activeSheet, normalizedRows, hiddenRows });

  // ---- Resize + zoom ----
  const { startResize } = useResize({ activeSheet, colWidths, rowHeights, setSheetDataByName });
  const zoomScale = useZoom(zoomScaleProp, zoom);

  // ---- Freeze panes ----
  const {
    frozenRows, frozenCols, handleFreeze, handleUnfreeze,
    freezeCtx, setFreezeCtx, handleCellContextMenu,
    gridWrapperRef, theadRef,
    measuredTheadHeight, frozenRowOffsets, frozenColOffsets,
  } = useFreezePanes({
    activeSheet, sheetDataByName,
    rowHeights, colWidths,
    hiddenRows, hiddenCols, isRowVisible,
    normalizedRowsLength: normalizedRows.length,
    zoomScale,
  });

  // ---- Scroll memory + keyboard nav + hyperlinks ----
  const { pendingLinkScrollRef } = useScrollMemory({ gridWrapperRef, activeSheet });

  useKeyboardNav({
    activeModel, normalizedRows, maxCols, colWidths, rowHeights,
    gridWrapperRef, setSelectedCell, setSelection,
  });

  const { handleLinkClick } = useHyperlinks({
    sheetNames, activeSheet, setActiveSheet, sheetDataByName,
    colWidths, rowHeights, gridWrapperRef,
    setSelectedCell, setSelection, pendingLinkScrollRef,
  });

  // ---- Header context menu ----
  const { headerCtx, handleHeaderContextMenu, closeHeaderContextMenu } = useHeaderContextMenu();

  // Reset header context menu when active sheet changes.
  useEffect(() => { closeHeaderContextMenu(); }, [activeSheet, closeHeaderContextMenu]);

  // ---- Imperative print API ----
  usePrint({ ref, workbookBufferRef, componentRef });

  // ---- Derived UI strings (formula bar) ----
  const selectionSummary = useMemo(() => {
    if (selectionType === "col") {
      const c1 = normalizedSelection.c1;
      const c2 = normalizedSelection.c2;
      return c1 === c2 ? `${toColumnLabel(c1)}:${toColumnLabel(c1)}` : `${toColumnLabel(c1)}:${toColumnLabel(c2)}`;
    }
    if (selectionType === "row") {
      const r1 = normalizedSelection.r1 + 1;
      const r2 = normalizedSelection.r2 + 1;
      return r1 === r2 ? `${r1}:${r1}` : `${r1}:${r2}`;
    }
    const rowsCount = normalizedSelection.r2 - normalizedSelection.r1 + 1;
    const colsCount = normalizedSelection.c2 - normalizedSelection.c1 + 1;
    return rowsCount === 1 && colsCount === 1 ? "" : `${rowsCount}R x ${colsCount}C`;
  }, [normalizedSelection, selectionType]);

  const selectedCellLabel = useMemo(() => {
    if (selectionType === "col") {
      const c1 = normalizedSelection.c1;
      const c2 = normalizedSelection.c2;
      return c1 === c2 ? `${toColumnLabel(c1)}:${toColumnLabel(c1)}` : `${toColumnLabel(c1)}:${toColumnLabel(c2)}`;
    }
    if (selectionType === "row") {
      const r1 = normalizedSelection.r1 + 1;
      const r2 = normalizedSelection.r2 + 1;
      return r1 === r2 ? `${r1}:${r1}` : `${r1}:${r2}`;
    }
    return `${toColumnLabel(selectedCell.col)}${selectedCell.row + 1}`;
  }, [selectedCell.col, selectedCell.row, selectionType, normalizedSelection]);

  const selectedCellText = useMemo(() => {
    if (!activeModel) return "";
    const formula = activeModel.formulas?.[`${selectedCell.row}:${selectedCell.col}`];
    if (formula) return formula;
    const value = activeModel.rows?.[selectedCell.row]?.[selectedCell.col];
    return value == null ? "" : String(value);
  }, [activeModel, selectedCell.col, selectedCell.row]);

  // ---- Early returns for loading / error / empty ----
  if (loading) return <div className="simple-excel-viewer-status" style={{ height }}>Loading Excel...</div>;
  if (error)   return <div className="simple-excel-viewer-status simple-excel-viewer-error" style={{ height }}>{error}</div>;
  if (sheetNames.length === 0) return <div className="simple-excel-viewer-status" style={{ height }}>No sheets found in this file.</div>;

  return (
    <div className="simple-excel-viewer" style={{ height }}>
      <FormulaBar
        selectedCellLabel={selectedCellLabel}
        selectionSummary={selectionSummary}
        selectedCellText={selectedCellText}
      />

      <Grid
        wrapperRef={gridWrapperRef}
        theadRef={theadRef}
        tableRef={componentRef}
        zoomScale={zoomScale}
        maxCols={maxCols}
        colWidths={colWidths}
        hiddenCols={hiddenCols}
        hiddenRows={hiddenRows}
        frozenCols={frozenCols}
        frozenColOffsets={frozenColOffsets}
        selectionType={selectionType}
        normalizedSelection={normalizedSelection}
        filters={filters}
        activeFilterCol={activeFilterCol}
        normalizedRows={normalizedRows}
        onToggleFilter={toggleFilterMenu}
        onApplyFilter={applyFilter}
        onCloseFilter={() => setActiveFilterCol(null)}
        onSelectColumn={selectColumn}
        onHeaderContextMenu={handleHeaderContextMenu}
        onStartResize={startResize}
        isLastVisibleBeforeHidden={isLastVisibleBeforeHidden}
        isLastVisibleBeforeHiddenRow={isLastVisibleBeforeHiddenRow}
        rowHeights={rowHeights}
        cellStyles={cellStyles}
        cellRichHtml={cellRichHtml}
        cellMerges={cellMerges}
        cellLinks={cellLinks}
        isRowVisible={isRowVisible}
        selectedCell={selectedCell}
        frozenRows={frozenRows}
        measuredTheadHeight={measuredTheadHeight}
        frozenRowOffsets={frozenRowOffsets}
        onSelectRow={selectRow}
        onStartSelection={startSelection}
        onExtendSelection={extendSelection}
        onLinkClick={handleLinkClick}
        onCellContextMenu={handleCellContextMenu}
        activeSheet={activeSheet}
        sheetImages={sheetImages}
        sheetCharts={sheetCharts}
      />

      <SheetTabs sheetNames={sheetNames} activeSheet={activeSheet} onSelect={setActiveSheet} />

      {headerCtx && (
        <HeaderContextMenu
          type={headerCtx.type}
          index={headerCtx.index}
          position={{ x: headerCtx.x, y: headerCtx.y }}
          hiddenSet={headerCtx.type === "col" ? hiddenCols : hiddenRows}
          onHide={headerCtx.type === "col" ? handleHideCol : handleHideRow}
          onUnhide={headerCtx.type === "col" ? handleUnhideCols : handleUnhideRows}
          onClose={closeHeaderContextMenu}
        />
      )}

      <FreezeContextMenu
        ctx={freezeCtx}
        frozenRows={frozenRows}
        frozenCols={frozenCols}
        onFreeze={handleFreeze}
        onUnfreeze={handleUnfreeze}
        onClose={() => setFreezeCtx(null)}
      />
    </div>
  );
});

export default memo(SimpleExcelViewer);
